// @flow
import React, { forwardRef } from 'react'

import makeStyles from '@material-ui/styles/makeStyles'
import InputBaseM from '@material-ui/core/InputBase'
import InputAdornmentM from '@material-ui/core/InputAdornment'
import { FormHelperText, Icon } from '@material-ui/core'
import { Clear, Done } from '@material-ui/icons'

import CustomLabel from './CustomLabel'

const useStyles = makeStyles({
  container: ({ fullWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    margin: '1.7vh 0 0 0',
    width: fullWidth ? '100%' : 'inhereit',
  }),
  input: {
    marginTop: 1,
    display: 'flex',
    alignItems: 'center',
  },
  iconButton: {
    padding: 1,
  },
  formHelperText: {
    color: '#FF3755',
    fontSize: '9px',
    width: 340,
    maxWidth: '50vw',
  },
})

type Props = {
  onChangeValue?: Function,
  infoLink?: string,
  infoText?: string,
  style?: Object,
  inputError?: boolean,
  errorDescription?: string,
  inputValid?: boolean,
  placeholder?: string,
  valueInput?: any,
  onFocus?: Function,
  label?: string,
  type?: string,
  disabled?: boolean,
  toUpperCase?: boolean,
  multiline?: boolean,
  inputProps?: Object,
  onClick?: Function,
  onBlur?: (any) => any,
  testId?: string,
  fullWidth?: boolean,
  customAdornment?: any,
}

/**
 *
 * @param valueInput  défini la donnée à afficher dans l'input, modifiable par onChangeValue
 * @param placeholder  message de précisions à l'utilisateur
 * @param multiline  active le type d'input multiline
 * @param type  défini le type HTML de l'input
 * @param label défini un label HTML personnalisé
 * @param disabled passe le composant en status désactivé
 * @param onChangeValue passe une fonction setter pour modifier le parent
 * @param onFocus déclenche la fonction en pramètre lors de l'évènement onFOcus
 * @param style surcharge du style de l'input
 * @param inputError passe le composant en status d'erreur si la validation du parent échoue
 * @param inputValid passe le composant en status valide si la validation du parent est bonne
 * @param errorDescription affiche le message d'erreur si la validation du parent échoue
 * @param inputProps permet de passer tout type de props compatibles avec l'input
 * @param toUpperCase passe le label en majuscule uniquement s'il est de type string
 * @param onClick déclenche une fonction lors de l'évènement onClick
 * @param onBlur déclenche une fonction lors de l'évènement onBlur
 * @param testId permet de cibler le composant lors de tests automatisés
 * @param rest toute autre props
 * @param fullWidth passe le container en 100% de width
 * @param ref transfert la ref lorsqu'elle est foward
 * @param infoText label du lien de l'helper
 * @param infoLink lien vers la page de l'helper (ex: mdp oublié)
 * @returns {JSX.Element}
 * @constructor
 */

const TextInput = (
  {
    valueInput = '',
    placeholder = '',
    multiline = false,
    type = 'text',
    label = '',
    disabled = false,
    onChangeValue = (noop) => noop,
    onFocus = (noop) => noop,
    style = {},
    inputError = false,
    inputValid = false,
    errorDescription = '',
    inputProps,
    toUpperCase = false,
    onClick = (noop) => noop,
    onBlur = (noop) => noop,
    testId = 'TextInputTester',
    fullWidth = false,
    customAdornment = '',
    infoLink = '',
    infoText = '',
    ...rest
  }: Props,
  ref
) => {
  if (multiline && type !== 'text') console.error('multiline must be typed as text only')

  const classes = useStyles({ fullWidth })

  return (
    <div className={classes.container}>
      {!!label && (
        <CustomLabel toUpperCase={toUpperCase} infoLink={infoLink} infoText={infoText}>
          {label}
        </CustomLabel>
      )}

      <div className={classes.input}>
        <InputBaseM
          inputRef={ref}
          style={style}
          onKeyDown={() => onClick && onClick()}
          onChange={(event) => onChangeValue(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          value={valueInput}
          placeholder={placeholder}
          autoFocus={false}
          type={type}
          inputProps={inputProps}
          multiline={multiline}
          rowsMax="4"
          fullWidth={multiline || fullWidth}
          onFocus={onFocus}
          data-testid={testId}
          endAdornment={
            !customAdornment
              ? (inputValid || inputError) && (
                  <InputAdornmentM className={classes.iconButton} aria-label="status">
                    {!!inputError ? (
                      <Clear style={{ color: '#FF3755' }} />
                    ) : (
                      <Done style={{ color: '#51b03a' }} />
                    )}
                  </InputAdornmentM>
                )
              : customAdornment
          }
          {...rest}
        />
      </div>
      {!!inputError && !!errorDescription && (
        <FormHelperText className={classes.formHelperText}>{errorDescription}</FormHelperText>
      )}
    </div>
  )
}
// Flow oblige à instancier avec un paramètre vide
// $FlowFixMe
export default forwardRef(TextInput)
